/**
 * <newton-calculator> — the force each gas spring needs, worked out from the lid's weight and
 * two distances, and handed to a force field elsewhere on the page (docs/gas-spring-finder.md
 * §2.12).
 *
 * It lives inside the <dialog> snippets/newton-calculator.liquid renders, and opens that dialog
 * itself: any button on the page whose data-newton-open holds the dialog's id. The caller owns
 * the button so the finder can put it beside its own force field, which is also why the click
 * listener sits on the document — the opener is outside this element.
 *
 * Interface, all from the markup the snippet renders:
 *   [data-newton-form]         the four fields, found by name: newton_kg, newton_l1, newton_l2,
 *                              newton_count
 *   [data-newton-error=<f>]    one line per field inside a role="alert" box, shown while that
 *                              field is empty or not positive; `max`, a second line for the
 *                              count, shown while it is above the count target's own max
 *   [data-newton-result]       role="status": "About 240 N per spring"
 *   [data-newton-use]          wraps the Use button; hidden until there is a result for the
 *                              values the fields hold
 *   data-force-target          id of the input the force fills
 *   data-count-target          id of the input the number of springs fills
 *   data-result, data-use      templates carrying %N%
 *   data-error-max             the `max` line's template, carrying %MAX%
 */
if (!customElements.get('newton-calculator')) {
  const FIELDS = ['kg', 'l1', 'l2', 'count'];
  const FORM_EVENTS = ['submit', 'input'];

  // As the old web shop tool's live script had it, to the digit (§4.9):
  // N = floor(kg × L1 × 12 ÷ (2 × n × L2)), then to the nearest 100 from 1000 N up and to the
  // nearest 10 below. The 12 stands where gravity would be 9.81; that is the built-in margin.
  const newtons = (kg, l1, l2, n) => {
    const raw = Math.floor((kg * l1 * 12) / (2 * n * l2));
    const step = raw >= 1000 ? 100 : 10;
    return Math.round(raw / step) * step;
  };

  // Written the way a person's edit arrives, so whatever listens to the target cannot tell the
  // difference. quantity-input.js fires the same pair when its buttons step the value.
  const fill = (input, value) => {
    if (!input) return;
    input.value = value;
    for (const type of ['input', 'change']) input.dispatchEvent(new Event(type, { bubbles: true }));
  };

  customElements.define(
    'newton-calculator',
    class NewtonCalculator extends HTMLElement {
      connectedCallback() {
        this.dialog = this.closest('dialog');
        this.form = this.querySelector('[data-newton-form]');
        if (!this.dialog || !this.form) return;

        // Calculate submits the form, and any edit makes a shown result stale: a result belongs
        // to the numbers that made it, and Use would otherwise hand over a force the fields no
        // longer say.
        this.onForm = (event) => {
          if (event.type === 'input') return this.show(null);
          event.preventDefault();
          this.calculate();
        };
        this.onClick = (event) => {
          const opener = event.target.closest('[data-newton-open]');
          if (opener && opener.dataset.newtonOpen === this.dialog.id) this.dialog.showModal();
          else if (this.querySelector('[data-newton-use]').contains(event.target)) this.use();
        };
        for (const type of FORM_EVENTS) this.form.addEventListener(type, this.onForm);
        document.addEventListener('click', this.onClick);
      }

      disconnectedCallback() {
        if (!this.onClick) return;
        for (const type of FORM_EVENTS) this.form.removeEventListener(type, this.onForm);
        document.removeEventListener('click', this.onClick);
      }

      calculate() {
        const inputs = FIELDS.map((f) => this.form.querySelector(`[name="newton_${f}"]`));
        const values = inputs.map((input) => Number(input.value));
        // The count becomes the springs per lid, so it stops where that input stops: its own max,
        // read from it. A force worked out for more springs than the lid gets is too weak.
        const max = +(document.getElementById(this.dataset.countTarget) || {}).max || Infinity;
        // Per field, the error line it shows, or ''. An empty field reads as 0 and anything
        // unreadable as NaN, so one comparison rejects both. The number of springs, the last
        // field, is also a whole number: it becomes a cart quantity.
        const errors = values.map((v, i) => (!(v > 0 && v < Infinity) || (i > 2 && v % 1) ? FIELDS[i] : i > 2 && v > max ? 'max' : ''));
        inputs.forEach((input, i) => input.setAttribute('aria-invalid', !!errors[i]));
        for (const line of this.querySelectorAll('[data-newton-error]')) {
          const key = line.dataset.newtonError;
          line.hidden = !errors.includes(key);
          if (key === 'max' && !line.hidden) line.textContent = this.dataset.errorMax.replace('%MAX%', max);
        }
        const first = errors.findIndex(Boolean);
        if (first < 0) return this.show(newtons(...values), values[3]);
        this.show(null);
        inputs[first].focus();
      }

      show(n, count) {
        const shown = n !== null;
        const text = (template) => (shown ? template.replace('%N%', n) : '');
        const use = this.querySelector('[data-newton-use]');
        this.n = n;
        this.count = count;
        use.hidden = !shown;
        use.querySelector('.btn__label').textContent = text(this.dataset.use);
        this.querySelector('[data-newton-result]').textContent = text(this.dataset.result);
      }

      // Close first: a closing modal hands focus back to its opener, which would undo the move.
      use() {
        const force = document.getElementById(this.dataset.forceTarget);
        fill(force, this.n);
        fill(document.getElementById(this.dataset.countTarget), this.count);
        this.dialog.close();
        if (force) force.focus();
      }
    }
  );
}
